'use client';

import { AnimatePresence, motion } from 'motion/react';
import { TypedLocale } from 'payload';
import React from 'react';

import { CMSLink, CMSLinkType } from '@/components/Link';
import { BlogSpotClient } from '@/globals/BlogSpot/ComponentClient';
import { Blog, BlogSpot as BlogSpotType } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import { BlogCard } from '../BlogCard';
import styles from './BlogList.module.scss';

type Props = {
  cards: Blog[];
  header?: boolean;
  link?: CMSLinkType;
  blogSpot?: BlogSpotType | false;
  title?: string;
  trackingCTA?: string;
  locale: TypedLocale;
};

const cardVariants = {
  hidden: {
    y: 50,
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
  },
};

export const BlogList: React.FC<Props> = ({
  blogSpot = false,
  cards,
  header = true,
  link,
  locale,
  title,
  trackingCTA,
}) => {
  const t = getDictionary(locale);

  if (!cards || cards.length === 0) {
    return null;
  }

  // Helper to split the cards into batches:
  //  - The first batch is always size 4.
  //  - After that, it alternates 3 and 4.
  const chunkCards = (allCards: Blog[]): Blog[][] => {
    const chunks: Blog[][] = [];
    let i = 0;
    let batchIndex = 0;

    while (i < allCards.length) {
      let size: number;
      if (batchIndex === 0 && blogSpot) {
        size = 3; // first batch is 3 cards
      } else {
        // alternate 3, 4, 3, 4, ...
        size = batchIndex % 2 === 1 ? 3 : 4;
      }

      chunks.push(allCards.slice(i, i + size));
      i += size;
      batchIndex++;
    }

    return chunks;
  };

  const batches = chunkCards(cards);

  return (
    <div className={'container'}>
      {header && (
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.name}>{t('blog.name')}</span>
            {title && <div className={cn(styles.title, 'h4')}>{title}</div>}
            {link && (
              <CMSLink
                {...link}
                appearance='level-3'
                className={cn(styles.link, 'button--medium')}
                tracking={{
                  type: 'Blog list',
                  headline: trackingCTA || title || '',
                  cta: t('blog.viewAll'),
                }}
              >
                {t('blog.viewAll')}
              </CMSLink>
            )}
          </div>
        </div>
      )}
      <div className={styles.wrapper}>
        <AnimatePresence>
          {batches.map((batch, batchIndex) => {
            // Decide layout class (e.g. normal vs. 3-in-a-row)
            const isThreeInARow = batchIndex !== 0 && batchIndex % 2 === 1;
            const sectionClass = isThreeInARow
              ? styles.threeInRowLayout
              : styles.normalLayout;

            return (
              <section
                key={`batch-${batch.length}-${batchIndex}`}
                className={cn(sectionClass, styles.list)}
              >
                {batch.map((card, cardIndex) => {
                  // Insert newsletter only in the first batch after the 3rd card
                  if (batchIndex === 0 && blogSpot && cardIndex === 2) {
                    return (
                      <React.Fragment key={`blog-card-${card.id}`}>
                        <motion.div
                          key={card.id}
                          className={styles.card}
                          initial='hidden'
                          whileInView='visible'
                          viewport={{ once: true }}
                          variants={cardVariants}
                          style={{ willChange: 'transform, opacity' }}
                          transition={{
                            bounce: 0,
                            ease: [0.625, 0.05, 0, 1],
                            duration: 0.5,
                          }}
                          layout
                        >
                          <BlogCard
                            isThreeInARow={isThreeInARow}
                            tracking={{
                              type: 'Blog list',
                              headline: trackingCTA || title || '',
                              cta: card.title,
                            }}
                            {...card}
                          />
                        </motion.div>
                        <motion.div
                          key={`blog-spot`}
                          className={styles.card}
                          initial='hidden'
                          whileInView='visible'
                          viewport={{ once: true }}
                          style={{ willChange: 'transform, opacity' }}
                          variants={cardVariants}
                          transition={{
                            bounce: 0,
                            ease: [0.625, 0.05, 0, 1],
                            duration: 0.5,
                          }}
                          layout
                        >
                          <BlogSpotClient blogSpot={blogSpot} />
                        </motion.div>
                      </React.Fragment>
                    );
                  }
                  return (
                    <motion.div
                      key={card.id}
                      className={styles.card}
                      initial='hidden'
                      whileInView='visible'
                      viewport={{ once: true }}
                      style={{ willChange: 'transform, opacity' }}
                      variants={cardVariants}
                      transition={{
                        bounce: 0,
                        ease: [0.625, 0.05, 0, 1],
                        duration: 0.5,
                      }}
                      layout
                    >
                      <BlogCard
                        className={cn(
                          isThreeInARow ? styles.threeInARowCard : ''
                        )}
                        isThreeInARow={isThreeInARow}
                        tracking={{
                          type: 'Blog list',
                          headline: trackingCTA || title || '',
                          cta: card.title,
                        }}
                        {...card}
                      />
                    </motion.div>
                  );
                })}
              </section>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};
